// @ts-nocheck
import React, { useState, useRef, useEffect, SetStateAction, useContext } from 'react';
import ConditionallyRender from 'react-conditionally-render';

import UserChatMessage from '../UserChatMessage/UserChatMessage';
import ChatbotMessage from '../ChatbotMessage/ChatbotMessage';

import {
  botMessage, /* This spreads out the flex items evenly across the main axis */
  userMessage,
  customMessage,
  createChatMessage,
} from './chatUtils';

import { ReactComponent as SendIcon } from '../../assets/icons/paper-plane-01.svg';
import { ReactComponent as LegalLinkIcon } from '../../assets/icons/icon-legallink.svg';

import './Chat.css';
import {
  ICustomComponents,
  ICustomMessage,
  ICustomStyles,
} from '../../interfaces/IConfig';
import { IMessage } from '../../interfaces/IMessages';
import { string } from 'prop-types';
import { ActionType, store } from '../../../../store';

interface IChatProps {
  setState: React.Dispatch<SetStateAction<any>>;
  widgetRegistry: any;
  messageParser: any;
  actionProvider: any;
  customComponents: ICustomComponents;
  botName: string;
  customStyles: ICustomStyles;
  headerText: string;
  customMessages: ICustomMessage;
  placeholderText: string;
  validator: (input: string) => Boolean;
  state: any;
  disableScrollToBottom: boolean;
  messageHistory: IMessage[] | string;
  parse?: (message: string) => void;
  actions?: object;
  messageContainerRef: React.MutableRefObject<HTMLDivElement>;
}

const Chat = ({
  state,
  setState,
  widgetRegistry,
  messageParser,
  parse,
  customComponents,
  actionProvider,
  botName,
  customStyles,
  headerText,
  customMessages,
  placeholderText,
  validator,
  disableScrollToBottom,
  messageHistory,
  actions,
  messageContainerRef,
}: IChatProps) => {
  const globalStore = useContext(store);

  const { messages } = state;

  const [input, setInputValue] = useState('');

  const scrollIntoView = () => {
    setTimeout(() => {
      if (messageContainerRef.current) {
        messageContainerRef.current.scrollTop =
          messageContainerRef?.current?.scrollHeight;
      }
    }, 50);
  };

  useEffect(() => {
    if (disableScrollToBottom) return;
    scrollIntoView();
  });

  useEffect(() => {
    if (globalStore.state.submitCustomizedMessage === '') return;

    console.log('globalStore.state.submitCustomizedMessage', globalStore.state.submitCustomizedMessage);
    handleCustomizedMessageSubmit(globalStore.state.submitCustomizedMessage);
    globalStore.dispatch({ type: ActionType.SUBMIT_CUSTOMIZED_MESSAGE, payload: '' });
  }, [globalStore.state.submitCustomizedMessage]);

  const showAvatar = (messages: any[], index: number) => {
    if (index === 0) return true;

    const lastMessage = messages[index - 1];

    if (lastMessage.type === 'bot' && !lastMessage.widget) {
      return false;
    }
    return true;
  };

  const renderMessages = () => {
    return messages.map((messageObject: IMessage, index: number) => {
      if (botMessage(messageObject)) {
        return (
          <React.Fragment key={messageObject.id}>
            {renderChatbotMessage(messageObject, index)}
          </React.Fragment>
        );
      }

      if (userMessage(messageObject)) {
        return (
          <React.Fragment key={messageObject.id}>
            {renderUserMessage(messageObject)}
          </React.Fragment>
        );
      }

      if (customMessage(messageObject, customMessages)) {
        return (
          <React.Fragment key={messageObject.id}>
            {renderCustomMessage(messageObject)}
          </React.Fragment>
        );
      }
    });
  };

  const renderCustomMessage = (messageObject: IMessage) => {
    const customMessage = customMessages[messageObject.type];

    const props = {
      setState,
      state,
      scrollIntoView,
      actionProvider,
      payload: messageObject.payload,
      actions,
    };

    if (messageObject.widget) {
      const widget = widgetRegistry.getWidget(messageObject.widget, {
        ...state,
        scrollIntoView,
        payload: messageObject.payload,
        actions,
      });
      return (
        <>
          {customMessage(props)}
          {widget ? widget : null}
        </>
      );
    }

    return customMessage(props);
  };

  const renderUserMessage = (messageObject: IMessage) => {
    const widget = widgetRegistry.getWidget(messageObject.widget, {
      ...state,
      scrollIntoView,
      payload: messageObject.payload,
      actions,
    });
    return (
      <>
        <UserChatMessage
          message={messageObject.message}
          key={messageObject.id}
          customComponents={customComponents}
        />
        {widget ? widget : null}
      </>
    );
  };

  const renderChatbotMessage = (messageObject: IMessage, index: number) => {
    let withAvatar;
    if (messageObject.withAvatar) {
      withAvatar = messageObject.withAvatar;
    } else {
      withAvatar = showAvatar(messages, index);
    }

    const chatbotMessageProps = {
      ...messageObject,
      setState,
      state,
      customComponents,
      widgetRegistry,
      messages,
      actions,
    };

    if (messageObject.widget) {
      const widget = widgetRegistry.getWidget(chatbotMessageProps.widget, {
        ...state,
        scrollIntoView,
        payload: messageObject.payload,
        actions,
      });
      return (
        <>
          <ChatbotMessage
            customStyles={customStyles.botMessageBox}
            withAvatar={withAvatar}
            {...chatbotMessageProps}
            key={messageObject.id}
          />
          <ConditionallyRender
            condition={!chatbotMessageProps.loading}
            show={widget ? widget : null}
          />
        </>
      );
    }

    return (
      <ChatbotMessage
        customStyles={customStyles.botMessageBox}
        key={messageObject.id}
        withAvatar={withAvatar}
        {...chatbotMessageProps}
        customComponents={customComponents}
        messages={messages}
        setState={setState}
      />
    );
  };

  const resetInputAreaHeight = () => {
    console.log('resetInputAreaHeight')
    document.querySelector('.react-chatbot-kit-chat-input').style.height = 'auto';
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    resetInputAreaHeight();

    if (validator && typeof validator === 'function') {
      if (validator(input)) {
        handleValidMessage();
        if (parse) {
          return parse(input);
        }
        messageParser.parse(input);
      }
    } else {
      handleValidMessage();
      if (parse) {
        return parse(input);
      }
      messageParser.parse(input);
    }
  };

  const handleCustomizedMessageSubmit = (message: string) => {

    resetInputAreaHeight();

    if (validator && typeof validator === 'function') {
      if (validator(message)) {
        handleValidCustomizedMessage(message);
        if (parse) {
          return parse(message);
        }
        messageParser.parse(message);
      }
    } else {
      handleValidCustomizedMessage(message);
      if (parse) {
        return parse(message);
      }
      messageParser.parse(message);
    }

    resetInputAreaHeight();
  };

  const handleValidMessage = () => {
    setState((state: any) => ({
      ...state,
      messages: [...state.messages, createChatMessage(input, 'user')],
    }));

    scrollIntoView();
    setInputValue('');
  };

  const handleValidCustomizedMessage = (message: string) => {
    console.log('handleValidCustomizedMessage message', message)
    setState((state: any) => ({
      ...state,
      messages: [...state.messages, createChatMessage(message, 'user')],
    }));

    scrollIntoView();
    setInputValue('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent form submission
      // Insert newline at cursor position
      const cursorPosition = e.target.selectionStart;
      const textBeforeCursor = input.substring(0, cursorPosition);
      const textAfterCursor = input.substring(cursorPosition);
      setInputValue(`${textBeforeCursor}\n${textAfterCursor}`);

      // Move cursor to the right position after setting the new value
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd = cursorPosition + 1;
      }, 0);

      // Scroll to the bottom of the textarea
      e.target.scrollTop = e.target.scrollHeight;
    }
  };

  // Update input field size based on its content, limited to 3 lines
  const handleInput = (e) => {
    setInputValue(e.target.value);

    // Reset the height to shrink back if text is deleted or to calculate the new height properly
    resetInputAreaHeight();

    if(e.target.value !== '') {
      // Calculate the new height based on the scroll height, which represents the total height of the content
      let newHeight = e.target.scrollHeight;

      // Get the line height of the textarea to calculate the number of lines
      const lineHeight = parseInt(getComputedStyle(e.target).lineHeight, 10);

      // Calculate the number of lines in the textarea
      const numberOfLines = Math.floor(newHeight / lineHeight);

      if (numberOfLines > 1) {
        const maxHeight = lineHeight * 3; // Maximum height for 3 lines
        e.target.style.height = `${Math.min(newHeight, maxHeight)}px`; // Apply the new height or the max height for 3 lines
      }
    }
  };

  const customButtonStyle = { backgroundColor: '' };
  if (customStyles && customStyles.chatButton) {
    customButtonStyle.backgroundColor = customStyles.chatButton.backgroundColor;
  }

  let header = `${botName}`;
  if (headerText) {
    header = headerText;
  }

  let placeholder = 'Write your message here';
  if (placeholderText) {
    placeholder = placeholderText;
  }

  return (
    <div className="react-chatbot-kit-chat-container">
      <div className="react-chatbot-kit-chat-inner-container">
        <ConditionallyRender
          condition={!!customComponents.header}
          show={
            customComponents.header && customComponents.header(actionProvider)
          }
          elseShow={
            <div className="react-chatbot-kit-chat-header">{header}</div>
          }
        />

        <div
          className="react-chatbot-kit-chat-message-container"
          ref={messageContainerRef}
        >
          <ConditionallyRender
            condition={
              typeof messageHistory === 'string' && Boolean(messageHistory)
            }
            show={
              <div
                dangerouslySetInnerHTML={{ __html: messageHistory as string }}
              />
            }
          />

          {renderMessages()}
          <div style={{ paddingBottom: '15px' }} />
        </div>

        <div className="react-chatbot-kit-chat-input-container">
          <form
            className="react-chatbot-kit-chat-input-form"
            onSubmit={handleSubmit}
          >
            <textarea
              className="react-chatbot-kit-chat-input"
              placeholder={placeholder}
              value={input}
              onChange={handleInput}
              onKeyDown={handleKeyPress}
              style={{resize: 'none', overflow: 'auto'}} // Prevent manual resizing
              rows="1" // Start with a single line
            />
            <button
              className="react-chatbot-kit-chat-btn-send"
              style={customButtonStyle}
              disabled={!input.trim()}
            >
              <SendIcon className="react-chatbot-kit-chat-btn-send-icon" />
            </button>
          </form>
          <div className="copyright">
            <span className="powered-by">POWERED BY</span>
            <div className="branding">
              <span className="legallink-logo">
                <a href="https://www.legallink.info" target="_blank" rel="noopener noreferrer">
                  <LegalLinkIcon/>
                </a>
              </span>
                <span className="legallink">
                  <a href="https://www.legallink.info" target="_blank" rel="noopener noreferrer" style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    userSelect: 'none',
                    cursor: 'pointer',
                  }}>
                    legal link
                  </a>
                </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
